import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, BookOpen, CircleCheck, CircleDashed, Crosshair, Flame, Globe, LayoutGrid, Lock, Medal, Plus, Route, Share2,
  Target, TrendingUp, Trophy, Users,
} from 'lucide-react';
import { gamificationService } from '../services/api';
import { useCourse } from '../contexts/CourseContext';
import { useGamification } from '../contexts/GamificationContext';
import { AppShell, Button, Card, EmptyState, Loader, Segmented } from '../components/ui';
import ShareModal from '../components/ShareModal';
import { MedalModal } from '../components/achievements/MedalModal';
import { fmtXp, iconFor, tierIcon, tierOf } from '../utils/gamification';
import './Achievements.css';

const TRAIL_VIEW_KEY = 'ss.achievements.trailView';
const DAYS = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];
const SHOWCASE = 6;
const TIER_NEXT = { none: ['bronze', 50], bronze: ['silver', 70], silver: ['gold', 85] };
const TIER_NAME = { bronze: 'Bronze', silver: 'Prata', gold: 'Ouro' };

/** Conquistas (4a): banner, próximo nível, na mira, maestria por tópico, medalhas e ranking. */
function Achievements() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const { currentCourse, selectCourse } = useCourse();
  const gamification = useGamification();
  const refreshXp = gamification?.refresh;
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [scope, setScope] = useState('course');
  const [ranking, setRanking] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [modal, setModal] = useState(null);
  const [trailView, setTrailView] = useState(() => {
    try {
      return localStorage.getItem(TRAIL_VIEW_KEY) === 'snake' ? 'snake' : 'table';
    } catch {
      return 'table';
    }
  });
  const base = `/curso/${courseSlug}`;

  const changeTrailView = (view) => {
    setTrailView(view);
    try { localStorage.setItem(TRAIL_VIEW_KEY, view); } catch { /* sem armazenamento */ }
  };

  useEffect(() => { selectCourse(courseSlug); }, [courseSlug, selectCourse]);

  const load = () => {
    setError(false);
    gamificationService.getPage(courseSlug)
      .then(page => {
        setData(page);
        // Abrir a página conta como ter visto as medalhas novas (some o ponto do menu)
        return gamificationService.markSeen(courseSlug).then(() => refreshXp?.(courseSlug));
      })
      .catch(() => setError(true));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [courseSlug]);

  useEffect(() => {
    gamificationService.getRanking(courseSlug, scope).then(setRanking).catch(() => setRanking(null));
  }, [courseSlug, scope]);

  /** Para onde levar quem quer uma medalha. */
  const medalAction = (m) => {
    const k = m.key;
    if (k === 'streak_7' || k === 'streak_30') return { label: 'Estudar hoje', to: `${base}/estudo` };
    if (k === 'error_hunter') return { label: 'Abrir o caderno', to: `${base}/erros` };
    if (k === 'elephant_memory') return { label: 'Revisar flashcards', to: `${base}/flashcards` };
    if (k === 'first_lesson' || k === 'phase_done') return { label: 'Continuar aulas', to: `${base}/estudo` };
    if (k === 'topic_master' || k === 'polymath') return { label: 'Ver tópicos', to: `${base}/estudo` };
    return { label: 'Fazer um simulado', to: `${base}/config` };
  };

  const view = useMemo(() => (data ? derive(data, medalAction, base) : null), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <AppShell section="Conquistas" courseName={currentCourse?.name}>
        <EmptyState file="conquistas.xml" title="Não foi possível carregar suas conquistas"
                    description="Verifique sua conexão e tente de novo." action={<Button onClick={load}>Tentar de novo</Button>} />
      </AppShell>
    );
  }
  if (!data || !view) {
    return <AppShell section="Conquistas" courseName={currentCourse?.name}><Loader label="Carregando conquistas..." /></AppShell>;
  }

  const openMedal = (key, e) => {
    const i = data.achievements.findIndex(a => a.key === key);
    if (i >= 0) setModal({ index: i, y: e?.clientY });
  };

  return (
    <AppShell section="Conquistas" courseName={currentCourse?.name}>
      <Banner data={data} view={view} ranking={ranking} courseName={currentCourse?.name}
              onShare={() => setSharing(true)} onMedal={openMedal} />
      {sharing && (
        <ShareModal courseSlug={courseSlug} courseName={currentCourse?.name} kind="progress" page={data} onClose={() => setSharing(false)} />
      )}

      <div className="acq">
        <NextLevel data={data} base={base} navigate={navigate} />

        {view.aim.length > 0 && (
          <section className="acq-sec">
            <header className="acq-sec__head">
              <h2 className="acq-sec__title"><Crosshair size={15} aria-hidden="true" />Na mira</h2>
              <span className="acq-sec__aside">medalhas e selos mais perto, juntos</span>
            </header>
            <div className="acq-aim">
              {view.aim.map(a => <AimCard key={a.id} item={a} onOpen={() => navigate(a.action.to)} />)}
            </div>
          </section>
        )}

        <section className="acq-sec" data-tour="cq-trilha">
          <header className="acq-sec__head">
            <h2 className="acq-sec__title"><Route size={15} aria-hidden="true" />Maestria por tópico</h2>
            <span className="acq-counts">
              {[['gold', 'Ouro'], ['silver', 'Prata'], ['bronze', 'Bronze']].map(([k, l]) => (
                <span key={k}><i className={`is-${k}`} />{l} <b>{data.masteryCounts?.[k] ?? 0}</b></span>
              ))}
              <span><i className="is-none" />Sem selo <b>{view.noSeal}</b></span>
            </span>
            <Segmented ariaLabel="Visualização da maestria" value={trailView} onChange={changeTrailView}
                       options={[{ value: 'table', label: 'Tabela', icon: LayoutGrid }, { value: 'snake', label: 'Trilha', icon: Route }]} />
          </header>
          {data.phases.length === 0 ? (
            <EmptyState file="maestria.xml" title="Nenhum tópico neste curso" description="Os tópicos aparecem aqui quando o curso tiver conteúdo." />
          ) : trailView === 'snake' ? (
            <SnakeTrail phases={data.phases} />
          ) : (
            <MasteryTable phases={data.phases} onTopic={(t) => navigate(`${base}/estudo/${t.topicId}`)} />
          )}
          <p className="acq-foot">Marcas na barra: bronze 50% · prata 70% · ouro 85% + todas as aulas. Clique no tópico para abrir o estudo dele.</p>
        </section>

        <div className="acq-bottom">
          <section className="acq-sec" data-tour="cq-medalhas">
            <header className="acq-sec__head">
              <h2 className="acq-sec__title"><Medal size={15} aria-hidden="true" />Medalhas</h2>
              <span className="acq-sec__aside">{data.medalsUnlocked} de {data.medalsTotal} · {view.nearCount} perto</span>
            </header>
            <Card className="acq-medals">
              {data.achievements.map(m => <MedalTile key={m.key} m={m} onOpen={(e) => openMedal(m.key, e)} />)}
            </Card>
          </section>
          <section className="acq-sec" data-tour="cq-ranking">
            <header className="acq-sec__head">
              <h2 className="acq-sec__title"><Users size={15} aria-hidden="true" />Ranking da semana</h2>
              <Segmented ariaLabel="Ranking" value={scope} onChange={setScope}
                         options={[{ value: 'course', label: 'Este curso' }, { value: 'global', label: 'Geral' }]} />
            </header>
            <RankingCard ranking={ranking} />
          </section>
        </div>
      </div>

      {modal && (
        <MedalModal medals={data.achievements} index={modal.index} anchorY={modal.y} courseSlug={courseSlug}
                    onIndex={(i) => setModal(m => ({ ...m, index: i }))} onClose={() => setModal(null)} actionFor={medalAction} />
      )}
    </AppShell>
  );
}

/** Dados derivados: na mira (medalhas + selos de tópico), quantos sem selo, medalhas "perto". */
function derive(data, medalAction, base) {
  const topics = data.phases.flatMap(p => p.topics);
  const medalAim = data.achievements.filter(m => !m.unlocked && m.progress && m.progress.pct < 100).map(m => ({
    id: `m-${m.key}`, kind: 'Medalha', name: m.name, icon: m.icon, tone: 'lime',
    pct: m.progress.pct, value: m.progress.label, left: leftLabel(m.progress), dist: 1 - m.progress.pct / 100,
    action: medalAction(m),
  }));
  const sealAim = topics.filter(t => t.answered > 0 && TIER_NEXT[t.tier]).map(t => {
    const [next, target] = TIER_NEXT[t.tier];
    const missing = Math.max(0, target - t.pct);
    const lessonsLeft = next === 'gold' && t.pct >= target ? t.lessons - t.lessonsDone : 0;
    return {
      id: `t-${t.topicId}`, kind: 'Selo', name: `${TIER_NAME[next]} em ${t.name}`, icon: next === 'gold' ? 'crown' : 'medal', tone: next,
      pct: Math.min(100, Math.round((t.pct / target) * 100)), value: `${t.pct}%`,
      left: missing > 0 ? `faltam ${missing} p.p.` : lessonsLeft > 0 ? `faltam ${lessonsLeft} aulas` : 'quase lá',
      dist: missing / 100 + lessonsLeft * 0.02,
      action: { label: missing > 0 ? 'Simulado do tópico' : 'Continuar aulas', to: `${base}/estudo/${t.topicId}` },
    };
  });
  const aim = [...medalAim, ...sealAim].sort((a, b) => a.dist - b.dist).slice(0, 4);
  return {
    aim,
    noSeal: topics.filter(t => t.tier === 'none').length,
    nearCount: data.achievements.filter(m => !m.unlocked && (m.progress?.pct || 0) >= 50).length,
  };
}

function leftLabel(p) {
  if (!p) return '';
  const counted = p.label === `${p.current} de ${p.target}`;
  if (counted && p.target > p.current) {
    const n = p.target - p.current;
    return `faltam ${n}`;
  }
  return p.pct < 100 ? `faltam ${100 - p.pct}%` : '';
}

// ── A1. Banner ──────────────────────────────────────────────────────

function Banner({ data, view, ranking, courseName, onShare, onMedal }) {
  const { level, globalLevel } = data;
  const remaining = level.nextLevelXp - level.levelXp;
  const pct = level.nextLevelXp ? Math.round((level.levelXp / level.nextLevelXp) * 100) : 0;
  const week = data.week || [];
  const weekTotal = week.reduce((s, d) => s + d.xp, 0);
  const max = Math.max(1, ...week.map(d => d.xp));
  const unlocked = data.achievements.filter(m => m.unlocked);
  const slots = Math.max(0, SHOWCASE - unlocked.length);
  return (
    <section className="acq-banner" data-tour="cq-nivel">
      <div className="acq-banner__inner">
        <div className="acq-banner__main">
          <div className="acq-banner__top">
            <span className="acq-eyebrow">{courseName ? `${courseName} · ` : ''}Conquistas</span>
            <button type="button" className="acq-banner__share" onClick={onShare}><Share2 size={14} aria-hidden="true" />Compartilhar</button>
          </div>
          <div className="acq-level">
            <span className="acq-level__num">{level.level}</span>
            <div className="acq-level__info">
              <h1>Nível {level.level} · {data.levelName}</h1>
              <span className="acq-level__bar"><span style={{ width: `${pct}%` }} /></span>
              <span className="acq-level__caption">{fmtXp(level.levelXp)} / {fmtXp(level.nextLevelXp)} XP neste nível · faltam {fmtXp(remaining)} XP para o nível {level.level + 1}</span>
              <div className="acq-pills">
                <span><Globe size={14} aria-hidden="true" className="is-lime" /><b>{globalLevel.level}</b> nível geral · {fmtXp(globalLevel.xp)} XP em {data.coursesWithXp} {data.coursesWithXp === 1 ? 'curso' : 'cursos'}</span>
                <span><Flame size={14} aria-hidden="true" className="is-amber" /><b>{data.streak}</b> {data.streak === 1 ? 'dia seguido' : 'dias seguidos'} · recorde {data.longestStreak ?? data.streak}</span>
              </div>
            </div>
          </div>
          <div className="acq-showcase">
            <span className="acq-showcase__label">Vitrine</span>
            {unlocked.slice(0, SHOWCASE).map(m => {
              const Icon = iconFor(m.icon);
              return (
                <button key={m.key} type="button" className="acq-showcase__medal" title={m.name} aria-label={m.name} onClick={(e) => onMedal(m.key, e)}>
                  <Icon size={20} aria-hidden="true" />
                </button>
              );
            })}
            {Array.from({ length: slots }).map((_, i) => <span key={i} className="acq-showcase__slot" aria-hidden="true"><Plus size={14} /></span>)}
            <span className="acq-showcase__count">{data.medalsUnlocked} de {data.medalsTotal} · {view.nearCount} perto</span>
          </div>
        </div>
        <aside className="acq-week" aria-label="Esta semana">
          <div className="acq-week__head">
            <span className="acq-eyebrow">Esta semana</span>
            <strong>+{fmtXp(weekTotal)} XP</strong>
          </div>
          <div className="acq-week__bars">
            {week.map((d, i) => (
              <span key={d.date} className="acq-week__day">
                <span className={`acq-week__bar ${d.today ? 'is-today' : d.xp === 0 ? 'is-zero' : ''}`}
                      style={{ height: d.xp ? `${Math.max(8, (d.xp / max) * 100)}%` : '4px' }}
                      title={`${fmtXp(d.xp)} XP`} />
                <span className={`acq-week__label ${d.today ? 'is-today' : ''}`}>{DAYS[i]}</span>
              </span>
            ))}
          </div>
          <p className="acq-week__text">{rankingPhrase(ranking) || 'O ranking zera toda segunda. Cada aula, questão e flashcard conta.'}</p>
        </aside>
      </div>
    </section>
  );
}

/** "Faltam 180 XP para passar a Marina. Rafael está 30 XP atrás de você." */
function rankingPhrase(r) {
  if (!r || !r.entries?.length) return '';
  const me = r.entries.find(e => e.me);
  if (!me) return '';
  const above = r.entries.find(e => e.position === me.position - 1);
  const below = r.entries.find(e => e.position === me.position + 1);
  const first = (n) => n.split(' ')[0];
  const parts = [];
  if (above) parts.push(`Faltam ${fmtXp(above.xp - me.xp + 1)} XP para passar ${first(above.name)}.`);
  else if (me.position === 1 && me.xp > 0) parts.push('Você lidera a semana.');
  if (below) parts.push(`${first(below.name)} está ${fmtXp(me.xp - below.xp)} XP atrás de você.`);
  return parts.join(' ');
}

// ── A2. Próximo nível ───────────────────────────────────────────────

function NextLevel({ data, base, navigate }) {
  const n = data.nextLevel;
  if (!n) return null;
  const lessons = n.lessons || {};
  const paths = [
    {
      icon: BookOpen, title: `${lessons.count} ${lessons.count === 1 ? 'aula' : 'aulas'}`,
      hint: lessons.lessonName ? `próxima: ${lessons.lessonName}` : 'conclua as aulas do curso', xp: lessons.xp,
      to: lessons.lessonId ? `${base}/estudo/${lessons.topicId}/aula/${lessons.lessonId}` : `${base}/estudo`,
    },
    { icon: CircleCheck, title: `${n.questions.count} questões certas`, hint: 'num simulado rápido', xp: n.questions.xp, to: `${base}/config` },
    { icon: Target, title: '1 simulado aprovado', hint: 'acima de 61%', xp: n.exam.xp, to: `${base}/config` },
  ];
  return (
    <section className="acq-next">
      <header className="acq-sec__head">
        <h2 className="acq-sec__title"><TrendingUp size={15} aria-hidden="true" />Qualquer um destes te leva ao nível {data.level.level + 1}</h2>
        <span className="acq-sec__aside">faltam {fmtXp(n.remaining)} XP</span>
      </header>
      <div className="acq-next__paths">
        {paths.map(p => (
          <button key={p.title} type="button" className="acq-path" onClick={() => navigate(p.to)}>
            <span className="acq-path__icon"><p.icon size={17} aria-hidden="true" /></span>
            <span className="acq-path__text"><strong>{p.title}</strong><span>{p.hint}</span></span>
            <span className="acq-path__xp">+{fmtXp(p.xp)}</span>
          </button>
        ))}
      </div>
      <p className="acq-foot">{data.xpRules.map(r => `${r.label.toLowerCase()} +${r.xp}`).join(' · ')}</p>
    </section>
  );
}

// ── A3. Na mira ─────────────────────────────────────────────────────

function AimCard({ item, onOpen }) {
  const Icon = iconFor(item.icon);
  const color = item.tone === 'lime' ? 'var(--lime-600)' : `var(--${item.tone === 'gold' ? 'gold' : item.tone === 'silver' ? 'silver' : 'bronze'})`;
  return (
    <button type="button" className="acq-aim__card" onClick={onOpen}>
      <span className="acq-aim__head">
        <span className="acq-ring" style={{ background: `conic-gradient(${color} ${item.pct * 3.6}deg, var(--surface-muted) 0)` }}>
          <span style={{ color }}><Icon size={18} aria-hidden="true" /></span>
        </span>
        <span className="acq-aim__name">
          <span className={`acq-aim__kind is-${item.kind === 'Selo' ? 'seal' : 'medal'}`}>{item.kind}</span>
          <strong>{item.name}</strong>
        </span>
      </span>
      <span className="acq-aim__progress"><b>{item.value}</b><span>{item.left}</span></span>
      <span className="acq-aim__cta">{item.action.label}<ArrowRight size={14} aria-hidden="true" /></span>
    </button>
  );
}

// ── A4. Maestria em tabela ──────────────────────────────────────────

function MasteryTable({ phases, onTopic }) {
  return (
    <Card className="acq-table" padding="none">
      {phases.map(phase => {
        const sealed = phase.topics.filter(t => t.tier !== 'none').length;
        return (
          <div key={phase.fase} className="acq-table__row">
            <span className="acq-table__phase"><span>Fase</span><strong>{String(phase.fase).padStart(2, '0')}</strong></span>
            <div className="acq-table__topics">
              {phase.topics.map(t => <MasteryCell key={t.topicId} t={t} onClick={() => onTopic(t)} />)}
            </div>
            <span className={`acq-table__count ${sealed ? 'is-on' : ''}`}><strong>{sealed}/{phase.topics.length}</strong><span>com selo</span></span>
          </div>
        );
      })}
    </Card>
  );
}

function MasteryCell({ t, onClick }) {
  const tier = tierOf(t.tier);
  const hasTier = t.tier !== 'none';
  const started = !hasTier && t.answered > 0;
  const Icon = hasTier ? tierIcon(t.tier) : started ? CircleDashed : Lock;
  const status = hasTier ? `${tier.label} · ${t.pct}%` : started ? `${t.pct}% · faltam ${50 - t.pct} p.p.` : 'Não iniciado';
  const barColor = hasTier ? tier.fg : 'var(--warning)';
  return (
    <button type="button" className={`acq-cell is-${hasTier ? t.tier : started ? 'started' : 'idle'}`} onClick={onClick}
            title={`${t.name}: ${t.answered ? `${t.correct}/${t.answered} acertos` : 'sem questões respondidas'}${t.lessons ? ` · ${t.lessonsDone}/${t.lessons} aulas` : ''}`}>
      <span className="acq-cell__icon" style={hasTier ? { background: tier.bg, color: tier.fg } : undefined}><Icon size={14} aria-hidden="true" /></span>
      <span className="acq-cell__main">
        <strong>{t.name}</strong>
        <span className="acq-cell__row">
          <span className="acq-cell__bar">
            <span style={{ width: `${t.pct}%`, background: barColor }} />
            <i style={{ left: '50%' }} /><i style={{ left: '70%' }} /><i style={{ left: '85%' }} />
          </span>
          <span className="acq-cell__status" style={hasTier ? { color: tier.fg } : started ? { color: 'var(--warning)' } : undefined}>{status}</span>
        </span>
      </span>
    </button>
  );
}

// ── A5. Medalhas e ranking ──────────────────────────────────────────

function MedalTile({ m, onOpen }) {
  const Icon = iconFor(m.icon);
  const pct = m.progress?.pct || 0;
  return (
    <button type="button" className={`acq-medal ${m.unlocked ? 'is-won' : ''}`} onClick={onOpen}>
      <span className="acq-medal__disc" style={m.unlocked ? undefined : { background: `conic-gradient(var(--navy-100) ${pct * 3.6}deg, transparent 0)` }}>
        <span><Icon size={20} aria-hidden="true" /></span>
      </span>
      <strong>{m.name}</strong>
      <span>{m.unlocked ? `+${m.xp} XP` : m.progress?.label}</span>
    </button>
  );
}

function RankingCard({ ranking }) {
  if (!ranking) return <Card className="acq-rank"><Loader /></Card>;
  const empty = !ranking.entries.some(e => e.xp > 0);
  if (empty) {
    return (
      <Card className="acq-rank acq-rank--empty">
        <span className="acq-rank__ghosts" aria-hidden="true"><i /><i /><i /></span>
        <p>Quem estudar este curso na semana aparece aqui. O ranking zera toda segunda.</p>
      </Card>
    );
  }
  const phrase = rankingPhrase(ranking);
  return (
    <Card className="acq-rank" padding="none">
      {phrase && <p className="acq-rank__phrase"><TrendingUp size={15} aria-hidden="true" />{phrase}</p>}
      {ranking.entries.map(r => (
        <div key={`${r.position}-${r.name}`} className={`acq-rank__row ${r.me ? 'is-me' : ''}`}>
          <span className={`acq-rank__pos ${r.position <= 3 ? 'is-top' : ''}`}>{r.position}</span>
          <span className="acq-rank__avatar" aria-hidden="true">{r.initials}</span>
          <span className="acq-rank__who"><strong>{r.name}{r.me ? ' (você)' : ''}</strong><span>Nível {r.level}</span></span>
          <span className="acq-rank__xp">{fmtXp(r.xp)}<span> XP</span></span>
        </div>
      ))}
    </Card>
  );
}

/** Rótulo, cores e dica do tópico na trilha. Iniciado sem medalha fica em âmbar. */
function topicInfo(t) {
  const tier = tierOf(t.tier);
  const hasTier = t.tier !== 'none';
  const started = !hasTier && t.answered > 0;
  const status = hasTier
    ? `${tier.label} · ${t.pct}%`
    : started ? `${t.pct}% · faltam ${50 - t.pct} p.p.` : 'Não iniciado';
  const lessons = t.lessons > 0 ? ` · ${t.lessonsDone}/${t.lessons} aulas` : '';
  return {
    Icon: tierIcon(t.tier),
    status,
    state: hasTier ? 'tier' : started ? 'started' : 'idle',
    iconStyle: hasTier ? { background: tier.bg, color: tier.fg } : undefined,
    statusStyle: hasTier ? { color: tier.fg } : undefined,
    title: `${t.name}: ${t.answered ? `${t.correct}/${t.answered} acertos` : 'sem questões respondidas'}${lessons}`,
  };
}

/** Trilha em serpentina: cada fase é uma linha e a direção alterna, com curvas nas pontas. */
function SnakeTrail({ phases }) {
  return (
    <Card className="ach-snake">
      {phases.map((phase, i) => {
        const last = i === phases.length - 1;
        const turn = last ? null : i % 2 === 0 ? 'right' : 'left';
        return (
          <div key={phase.fase} className={`ach-snake__row ${i % 2 ? 'is-reverse' : ''}`}>
            <span className="ach-snake__line" aria-hidden="true" />
            {turn && <span className={`ach-snake__turn ach-snake__turn--${turn}`} aria-hidden="true" />}
            <span className="ach-snake__phase">
              <span>Fase</span>
              <strong>{String(phase.fase).padStart(2, '0')}</strong>
            </span>
            {phase.topics.map(t => <SnakeTopic key={t.topicId} topic={t} />)}
            {last && (
              <span className="ach-snake__goal" title="Fim da trilha">
                <Trophy size={24} aria-hidden="true" />
              </span>
            )}
          </div>
        );
      })}
    </Card>
  );
}

function SnakeTopic({ topic: t }) {
  const { Icon, status, state, iconStyle, statusStyle, title } = topicInfo(t);
  return (
    <div className={`ach-snake__topic is-${state}`} title={title}>
      <span className="ach-topic__icon" style={iconStyle}><Icon size={14} aria-hidden="true" /></span>
      <span className="ach-topic__text">
        <strong>{t.name}</strong>
        <span style={statusStyle}>{status}</span>
      </span>
    </div>
  );
}


export default Achievements;
